import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services';
import { v4 as uuidv4 } from 'uuid';
import { Button, Modal } from 'antd';
import _ from 'lodash';
import { config } from '../../constants';
import { getToken } from '../../services/api/user';

const shareBotResources = {
    organization: 'ORGANIZATION',
    worldKnowledge: 'WORLD_KNOWLEDGE'
};

const botModalDefault = {
    visible: false,
    file: {}
};

function ChatView({ user, conversationHistory, setConversationHistory, activeBotResource, botAvatar }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [botModal, setBotModal] = useState(botModalDefault);

    const endOfConversationRef = useRef(null);

    const getUserAvatar = (user) => {
        if (user && user.info && user.info.avatar) {
            return user.info.avatar.resizes && user.info.avatar.resizes.thumbnail || user.info.avatar.link || null;
        }
        return null;
    };

    useEffect(() => {
        if (conversationHistory.length > 0) {
            const lastMessage = conversationHistory[conversationHistory.length - 1];
            if (lastMessage.type === 'user' && !loading) {
                handleBotResponse(lastMessage.text);
            }
        }

    }, [conversationHistory]);

    useEffect(() => {
        if (endOfConversationRef.current) {
            endOfConversationRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [loading, conversationHistory]);

    const handleBotResponse = async (question) => {
        setLoading(true);
        setError(false);

        const org_id = String(user.info.active_organization_id);
        const conversationalHistory = conversationHistory.map(msg => ({
            type: msg.type,
            text: msg.text || msg.answer
        }));

        const messageId = uuidv4();

        const botMessagePlaceholder = {
            id: messageId,
            type: 'bot',
            source: activeBotResource,
            text: '',
            timestamp: new Date(),
            avatar: botAvatar,
            reference: {}
        };

        setConversationHistory(prev => [...prev, botMessagePlaceholder]);

        if (activeBotResource === shareBotResources.organization) {
            try {
                const url = config.questionAnswerAiUrl;
                const token = await getToken();

                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Accept': 'text/event-stream',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        params: {
                            org_id,
                            question,
                            conversational_history: conversationalHistory
                        }
                    }),
                });

                if (!response.ok || !response.body) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder('utf-8');
                let accumulatedText = '';
                let references = {};

                const readStream = async () => {
                    let buffer = '';
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });

                        const lines = buffer.split('\n');

                        // Leave the last line in buffer (incomplete)
                        buffer = lines.pop();

                        for (let line of lines) {
                            if (!line.trim()) continue;

                            try {
                                const parsed = JSON.parse(line);
                                console.log({
                                    parsed
                                })
                                if (parsed.chunk) {
                                    accumulatedText += parsed.chunk;
                                } else if (parsed.completed) {
                                    references = parsed.reference || {};
                                }

                                setConversationHistory(prev =>
                                    prev.map(msg =>
                                        msg.id === messageId
                                            ? { ...msg, text: accumulatedText, reference: references }
                                            : msg
                                    )
                                );
                            } catch (err) {
                                console.warn('Failed to parse line', line, err);
                            }
                        }
                    }

                    if (buffer.trim()) {
                        try {
                            const parsed = JSON.parse(buffer);
                            console.log({
                                parsed
                            })
                            if (parsed.chunk) {
                                accumulatedText += parsed.chunk;
                            } else if (parsed.completed) {
                                references = parsed.reference || {};
                            }

                            setConversationHistory(prev =>
                                prev.map(msg =>
                                    msg.id === messageId
                                        ? { ...msg, text: accumulatedText, reference: references }
                                        : msg
                                )
                            );
                        } catch (err) {
                            console.warn('Failed to parse remaining buffer', buffer, err);
                        }
                    }

                    setLoading(false);
                };


                await readStream();
                setLoading(false);

            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Streaming error:', err);
                    setError(true);
                    setConversationHistory(prev => {
                        const exists = prev.some(msg => msg.id === messageId);
                        if (exists) {
                            return prev.map(msg =>
                                msg.id === messageId
                                    ? { ...msg, text: 'Network Error. Please Try Again!' }
                                    : msg
                            );
                        } else {
                            return [...prev, {
                                id: messageId,
                                type: 'bot',
                                source: shareBotResources.organization,
                                text: 'Network Error. Please Try Again!',
                                timestamp: new Date(),
                                avatar: botAvatar
                            }];
                        }
                    });
                    setLoading(false);
                }
            }
        } else {
            let tryCount = 0;
            const maxTries = 2;

            const makeRequest = async () => {
                try {
                    const res = await api.audioSynthesizer.openAIBot({
                        org_id,
                        question,
                        conversational_history: conversationalHistory
                    });

                    const body = res.data.body && res.data.body[0];
                    if (!body) throw new Error('No response body');

                    setConversationHistory(prev => prev.map(msg =>
                        msg.id === messageId
                            ? { ...msg, ...body }
                            : msg
                    ));

                    setError(false);
                } catch {
                    if (tryCount < maxTries) {
                        tryCount++;
                        await makeRequest();
                    } else {
                        setError(true);

                        setConversationHistory(prev => {
                            return prev.map(msg =>
                                msg.id === messageId
                                    ? { ...msg, text: 'Network Error. Please Try Again!' }
                                    : msg
                            );
                        })
                    }
                } finally {
                    setLoading(false);
                }
            };

            await makeRequest();
        }
    };

    const handleOpenReferenceFile = useCallback(() => {
        if (botModal.file && botModal.file.signed_url) {
            window.open(botModal.file.signed_url, '_blank');
        }
    }, [botModal]);

    const handleCloseModal = () => {
        setBotModal(botModalDefault);
    };

    const handleOpenFileReference = (file) => {
        setBotModal({
            visible: true,
            file
        });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Add a function to parse text and make references clickable
    // Add a function to parse text and make references clickable
    const parseMessageWithReferences = (text, references) => {
        if (!text || !references || _.isEmpty(references)) {
            return text;
        }

        let result = [];

        // Split the text to find the References section
        const parts = text.split('References:');

        if (parts.length <= 1) {
            // No References section found, return the original text
            return text;
        }

        // Add the text before the References section
        result.push(parts[0]);
        result.push('References:');

        // Process the references section
        const referencesSection = parts[1];

        // Handle case where referencesSection might be null or undefined
        if (!referencesSection) {
            return result;
        }

        // Trim any leading/trailing whitespace and split by newlines
        // This handles cases where there might be empty newlines
        const referenceLines = referencesSection.trim().split(/\n+/).filter(line => line.trim() !== '');

        // Process each line in the references section
        const processedLines = referenceLines.map(line => {
            // Match lines with reference numbers and filenames (allowing any file extension)
            // This pattern looks for [number] followed by any text that might include a file extension
            const refMatch = line.match(/^\s*\[(\d+)\]\s+(.+?\.(pdf|docx|xlsx|pptx|txt|csv|rtf|doc|xls|ppt|html|jpg|jpeg|png|gif))\b/i);

            if (refMatch) {
                const refNumber = refMatch[1];
                const fileName = refMatch[2];

                // Only process if we have this reference in our references object
                if (references[refNumber]) {
                    return (
                        <div key={`ref-line-${refNumber}`} className="reference-link"
                            onClick={() => handleOpenFileReference(references[refNumber])}
                            style={{ cursor: 'pointer', color: '#5B6EE2', textDecoration: 'underline' }}
                        >
                            <span>{`[${refNumber}] `}</span>
                            <span>
                                {fileName}
                            </span>
                        </div>
                    );
                }
            }

            // If no file extension match, try a more general pattern for reference numbers
            const simpleRefMatch = line.match(/^\s*\[(\d+)\]\s+(.+)/);
            if (simpleRefMatch) {
                const refNumber = simpleRefMatch[1];
                const content = simpleRefMatch[2];

                // Only process if we have this reference in our references object
                if (references[refNumber]) {
                    return (
                        <div key={`ref-line-${refNumber}`} className="reference-link"
                            onClick={() => handleOpenFileReference(references[refNumber])}
                            style={{ cursor: 'pointer', color: '#5B6EE2', textDecoration: 'underline' }}
                        >
                            <span>{`[${refNumber}] `}</span>
                            <span>
                                {content}
                            </span>
                        </div>
                    );
                }
            }

            // Return the original line if no match or reference not found
            return line;
        });

        // Add the processed references section
        result.push(processedLines);

        return result;
    };

    console.log({
        conversationHistory,
    })

    return (
        <div className="chat-view">
            {conversationHistory.map((msg, index) => (
                <div key={msg.id || index} className={`message-container ${msg.type === 'user' ? 'user-message' : 'bot-message'}`}>
                    {msg.type === 'bot' && (
                        <div className="avatar-container">
                            <img src={msg.avatar} alt="Bot" className="avatar" />
                        </div>
                    )}

                    <div className="message-content-wrapper">
                        <div className="message-bubble">
                            <div className="message-content">
                                {!msg.text && loading && msg.type === 'bot' && index === conversationHistory.length - 1 ? (
                                    <div className="loading-indicator">
                                        <div className="dot"></div>
                                        <div className="dot"></div>
                                        <div className="dot"></div>
                                    </div>
                                ) : (
                                    msg.type === 'bot' && msg.reference && !_.isEmpty(msg.reference)
                                        ? parseMessageWithReferences(msg.text || msg.answer || '', msg.reference)
                                        : (msg.text || msg.answer || '')
                                )}
                            </div>

                            {msg.reference && !_.isEmpty(msg.reference) && msg.source === shareBotResources.worldKnowledge && (
                                <div className="reference-list">
                                    <span className="reference-title">References</span>
                                    <ul>
                                        {Object.keys(msg.reference).map(item => (
                                            <li key={item} onClick={() => handleOpenFileReference(msg.reference[item])}>
                                                <span style={{ flexShrink: 0, marginRight: '4px' }}>[{item}]</span>
                                                <span>{msg.reference[item].file_title || msg.reference[item].pdf_name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="message-time">{formatTime(msg.timestamp)}</div>
                    </div>

                    {msg.type === 'user' && (
                        <div className="avatar-container">
                            <img src={msg.avatar} alt="User" className="avatar" />
                        </div>
                    )}
                </div>
            ))}

            <Modal
                visible={botModal.visible}
                title={botModal.file.file_title || botModal.file.pdf_name}
                onCancel={handleCloseModal}
                footer={[
                    <Button key="open" type="primary" onClick={handleOpenReferenceFile}>
                        Open File
                    </Button>
                ]}
                width="80%"
                centered
            >
                <div className="reference-content-container">
                    <h1>Contexts</h1>
                    {botModal.file.context && botModal.file.context.map((item, index) => (
                        <div key={index} className="context-item">
                            <h3>{index + 1}.</h3>
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            </Modal>

            <div ref={endOfConversationRef} className="conversation-end"></div>
        </div>
    );
}

export default ChatView;
